import { useContext, useState, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Button,
  TextField,
  Typography,
  Stack,
  FormControl,
  Select,
  MenuItem,
  // FormHelperText,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import moment from "moment";
import { useMutation } from "@apollo/client";
//Import from project
import "../../../Style/dialogstyle.scss";
import { AuthContext } from "../../../Context/AuthContext";
import { DialogTitleComp } from "../../../Utils/Component";
import { CREATE_PAYMENT } from "../../../Schema/Invoice";
import { currencyFormat, minuseTwoNumber } from "../../../Function/DynamicFn";
import { SelectDatePicker } from "../../Include/SelectDatePicker/SelectDatePicker";
// import useGetExchangeRate from "../../../Hooks/useGetExchangeRate";

export default function Payment({
  open,
  editData,
  setRefetch,
  handleClose,
  hideBackdrop,
}) {
  // Change Language
  const { t, setAlert } = useContext(AuthContext);

  const [inputReceivedAmount, setInputReceivedAmount] = useState(0);
  const [receivedAmount, setReceivedAmount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [inputType, setInputType] = useState("dollar");
  const [createPayment] = useMutation(CREATE_PAYMENT, {
    onCompleted: ({ createPayment }) => {
      setLoading(false);
      if (createPayment?.isSuccess === true) {
        setRefetch();
        setAlert(true, "success", createPayment?.message);
        handleClose();
        resetForm();
      } else {
        setAlert(true, "error", createPayment?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  // console.log("editData::", editData)

  const validationSchema = Yup.object().shape({
    paidAmount: Yup.number().min(1).max(editData?.totalOwe),
    date: Yup.string(),
    remark: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      paidAmount: 0,
      date: moment(),
      remark: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      // console.log("editData::", editData);
      createPayment({
        variables: {
          // ...values,
          invoiceId: editData?._id,
          date: moment(values?.date).format("YYYY-MM-DD"),
          paidAmount: receivedAmount,
          remark: values?.remark,
        },
      });
    },
  });

  const {
    values,
    errors,
    touched,
    setFieldValue,
    resetForm,
    handleSubmit,
    getFieldProps,
  } = formik;

  // console.log("values::", values);

  useEffect(() => {
    if (inputReceivedAmount) {
      setFieldValue("paidAmount", receivedAmount);
    } else {
      setFieldValue("paidAmount", 0);
    }
  }, [inputReceivedAmount]);

  useEffect(() => {
    if (inputReceivedAmount && inputType === "dollar") {
      setReceivedAmount(inputReceivedAmount);
    } else {
      setReceivedAmount(inputReceivedAmount / editData?.exchangeRate);
    }
  }, [inputReceivedAmount, inputType]);

  return (
    <Dialog
      fullWidth
      open={open}
      className="dialog-container"
      maxWidth="sm"
      hideBackdrop={hideBackdrop}
    >
      <DialogTitleComp
        title={t("modal_title_pay_invoice")}
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleClose}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_date")}
                </Typography>
                <SelectDatePicker
                  value={values?.date}
                  onChange={(val) => setFieldValue("date", val || "")}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_exchange_rate")}
                </Typography>
                <Stack
                  sx={{
                    width: "100%",
                    height: "40px",
                    bgcolor: "#F2F2F2",
                    padding: "8.5px 14px",
                    borderRadius: 1,
                  }}
                  direction="row"
                  justifyContent="space-between"
                >
                  <Typography className={t("class-name-field-title")}>
                    {t("thead_exchange_rate")} :
                  </Typography>
                  <Typography
                    className={t("class-name-field-title")}
                    sx={{ fontWeight: "bold", color: "red !important" }}
                  >
                    {editData?.exchangeRate} ៛
                  </Typography>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_amount_type")}
                </Typography>
                <FormControl fullWidth size="small" className={"text-field"}>
                  <Select
                    value={inputType}
                    onChange={(e) => {
                      setInputType(e?.target?.value);
                      setInputReceivedAmount(0);
                    }}
                  >
                    <MenuItem value={"dollar"}>Dollar</MenuItem>
                    <MenuItem value={"riel"}>Riel</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_input_amount")}
                </Typography>
                {/* <TextField
                  type="number"
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <Typography className={t("class-name-field-title")}>
                        $
                      </Typography>
                    ),
                  }}
                  className="text-field"
                  placeholder={t("thead_input_amount")}
                  {...getFieldProps("paidAmount")}
                  error={Boolean(touched?.paidAmount && errors?.paidAmount)}
                  helperText={touched?.paidAmount && errors?.paidAmount}
                /> */}
                <TextField
                  type="number"
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <Typography className={t("class-name-field-title")}>
                        {inputType === "dollar" ? "$" : "៛"}
                      </Typography>
                    ),
                  }}
                  className="text-field"
                  placeholder={t("thead_input_amount")}
                  value={inputReceivedAmount}
                  onChange={(e) => {
                    const inputVal = parseFloat(e.target.value);
                    if (inputVal <= 0) {
                      setInputReceivedAmount(0);
                    } else {
                      setInputReceivedAmount(inputVal);
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}></Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Stack
                  sx={{
                    width: "100%",
                    height: "40px",
                    bgcolor: "#F2F2F2",
                    padding: "8.5px 14px",
                    borderRadius: 1,
                  }}
                  direction="row"
                  justifyContent="space-between"
                >
                  <Typography className={t("class-name-field-title")}>
                    {t("thead_total_amount")} :
                  </Typography>
                  <Typography
                    className={t("class-name-field-title")}
                    sx={{ fontWeight: "bold" }}
                  >
                    $ {currencyFormat(editData?.totalAmount)}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}></Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Stack
                  sx={{
                    width: "100%",
                    height: "40px",
                    bgcolor: "#F2F2F2",
                    padding: "8.5px 14px",
                    borderRadius: 1,
                  }}
                  direction="row"
                  justifyContent="space-between"
                >
                  <Typography className={t("class-name-field-title")}>
                    {t("thead_payment_amount")} :
                  </Typography>
                  <Typography
                    className={t("class-name-field-title")}
                    sx={{ fontWeight: "bold", color: "green !important" }}
                  >
                    ${" "}
                    {receivedAmount
                      ? currencyFormat(
                          editData?.totalAmount -
                            editData?.totalOwe +
                            receivedAmount
                        )
                      : currencyFormat(
                          editData?.totalAmount - editData?.totalOwe
                        )}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}></Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Stack
                  sx={{
                    width: "100%",
                    height: "40px",
                    bgcolor: "#F2F2F2",
                    padding: "8.5px 14px",
                    borderRadius: 1,
                  }}
                  direction="row"
                  justifyContent="space-between"
                >
                  <Typography className={t("class-name-field-title")}>
                    {t("thead_owe_amount")} :
                  </Typography>
                  <Typography
                    className={t("class-name-field-title")}
                    sx={{ fontWeight: "bold", color: "red !important" }}
                  >
                    ${" "}
                    {currencyFormat(
                      minuseTwoNumber(editData?.totalOwe, receivedAmount)
                    )}
                  </Typography>
                </Stack>
              </Grid>

              <Grid item xs={12} md={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_remark")}
                </Typography>
                <TextField
                  minRows={2}
                  multiline
                  fullWidth
                  size="small"
                  className="text-field"
                  placeholder={t("thead_remark")}
                  {...getFieldProps("remark")}
                />
              </Grid>

              <Grid item xs={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    onClick={handleSubmit}
                    className={t("class-btn-create")}
                  >
                    {t("payment")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
